import { useEffect, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'

import { RoutingPaths } from 'app/routes/routes'

import { MplusPayment } from 'widgets/mplus/mplus-payment/ui'
import { MplusUnactive } from 'widgets/mplus/mplus-payment/ui/components/mplus-unactive'

import {
  GuideDescription,
  GuideDetails,
  GuideHeader,
  GuideInfo,
  GuideStatus,
  TimeAndPlace,
} from 'entities/excursions/components'
import { orderExcursionsStore } from 'entities/excursions/models/order-excursions/order-excursions-store'
import { mplusStore } from 'entities/mplus/module/mplus-store'

import { BuyTicketApi } from 'shared/api/BuyTicket'
import { ExcursionsMyOrder } from 'shared/api/excursions'
import { MyOrderExcursion } from 'shared/api/excursions/list/model'
import { CreditInfo } from 'shared/api/mplus/credit-info'
import {
  StatusPurchaseExcursion,
  WHATSAPP_NUMBER,
  WH_LINK,
} from 'shared/constants'
import ErrorImage from 'shared/images/error.png'
import GhostImage from 'shared/images/ghost.png'
import { openDeepLink } from 'shared/lib/deep-link-open'
import { getErrorMessage } from 'shared/lib/errorText'
import { openExternalUrl } from 'shared/lib/external-open'
import { startPaymentFromConfirmResult } from 'shared/lib/payment-start'
import { Button } from 'shared/ui/button'
import { Carousel } from 'shared/ui/carousel'
import { useDialogBottomSheet } from 'shared/ui/dialog-bottom-sheet/lib'
import { HeaderGoBack } from 'shared/ui/header-go-back'
import { Loader } from 'shared/ui/loader'
import { PopupContent } from 'shared/ui/poup-content'

import s from './styles.module.scss'

const MyOrder = () => {
  const { t } = useTranslation()
  const { id } = useParams()

  const navigate = useNavigate()

  const [loading, setLoading] = useState<boolean>(false)

  const { closeDialog, openDialog } = useDialogBottomSheet()

  const [excursionDetails, setExcursionDetails] =
    useState<MyOrderExcursion | null>(null)

  const getCurrentExcursion = async () => {
    const { data: excursion } = await ExcursionsMyOrder.getMyOrderExcursion(id!)

    setExcursionDetails(excursion?.data)
  }

  const contactWithUs = () => {
    openExternalUrl(`${WH_LINK}${WHATSAPP_NUMBER}`)
  }

  const purchaseMplus = async (day: number) => {
    setLoading(true)

    try {
      const { data: response } = await CreditInfo.initPayMplus({
        payment_day: day,
        service_type: 'excursion_orders',
        service_id: String(excursionDetails?.id),
      })

      if (response?.data?.redirect_url) {
        openDeepLink(response?.data?.redirect_url)
      }
    } catch (err) {
      setLoading(false)

      const errorText = getErrorMessage(err)

      onShowError(errorText)
    }
  }

  const purchaseRegular = async () => {
    setLoading(true)

    try {
      const { data } = await BuyTicketApi.buyTicketConfirm({
        type: 'excursion_orders',
        id: String(excursionDetails?.id),
      })
      await startPaymentFromConfirmResult(data)
    } catch (err) {
      const errorText = getErrorMessage(err)

      onShowError(errorText)
    } finally {
      setLoading(false)
    }
  }

  const onShowError = (errorMessage?: string) => {
    openDialog({
      child: (
        <PopupContent
          image={ErrorImage}
          title={t('error')}
          description={errorMessage || t('please_tey_again')}
          button={t('try_again')}
          onClick={closeDialog}
        />
      ),
      isDialog: false,
    })
  }

  const onOpenMplus = () => {
    mplusStore.removeExcursionData()

    orderExcursionsStore.onPeopleQuantityChange(
      excursionDetails?.people_quantity!,
    )
    orderExcursionsStore.onTotalPriceChange(
      Number(excursionDetails?.total_price),
    )

    navigate(RoutingPaths.excursion.mplus.path())
  }

  useEffect(() => {
    if (id) {
      getCurrentExcursion()
    }
  }, [])

  const totalPrice = excursionDetails?.excursion.is_mplus
    ? excursionDetails?.excursion.mplus_amount
    : excursionDetails?.total_price

  return (
    <>
      {!excursionDetails?.excursion || loading ? (
        <Loader
          type="excursions"
          marginTop={200}
        />
      ) : (
        <>
          <HeaderGoBack
            title={t('detail')}
            buttonWhite
          />

          <Carousel
            images={excursionDetails?.excursion.excursion_images}
            errorImage={GhostImage}
          />

          <div className="container">
            <div className={s.content}>
              <GuideStatus
                isExcursion
                status={excursionDetails?.status}
              />

              <GuideHeader excursionDetails={excursionDetails?.excursion} />

              <GuideInfo
                isMyOrders
                excursionDetails={excursionDetails?.excursion}
                totalPrice={String(totalPrice)}
                bonusAmount={excursionDetails.bonus_amount}
              />

              <GuideDescription
                excursionDetails={excursionDetails?.excursion}
              />

              <GuideDetails excursionDetails={excursionDetails?.excursion} />

              <TimeAndPlace excursionDetails={excursionDetails?.excursion} />

              <div className={s.paymentBlock}>
                {StatusPurchaseExcursion.CREATED.key ===
                excursionDetails?.status ? (
                  <MplusPayment
                    price={excursionDetails.total_price}
                    tranchePrice={excursionDetails?.tranche_amount}
                    countPassengers={excursionDetails.people_quantity}
                    onPayMplus={purchaseMplus}
                    onOpenMplus={onOpenMplus}
                  >
                    <MplusUnactive
                      price={excursionDetails.total_price}
                      countPassengers={excursionDetails.people_quantity}
                      onSubmit={purchaseRegular}
                    />
                  </MplusPayment>
                ) : null}

                {excursionDetails?.excursion?.company_guide?.phone_number && (
                  <div className={s.buyTicket}>
                    <Button
                      variant="transparent"
                      onClick={contactWithUs}
                    >
                      <span className={s.button}>{t('contact_with')}</span>
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </>
  )
}

export default MyOrder
